"use client";

import { c as _c } from "react/compiler-runtime";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import { useEffect, useCallback, useMemo, useRef, useState, useImperativeHandle, type Ref, type ReactNode } from "react";
import { useChapterSave, type ChapterSaveController } from "@/hooks/use-chapter-save";
import { useEditorNavigation } from "@/hooks/use-editor-navigation";
import type { ChapterDocumentKind } from "@/lib/editor/chapter-versions";
import { useIsMobile } from "@/hooks/use-mobile";
import { EditorToolbar } from "@/components/editor/editor-toolbar";
import { useEditorBackend } from "@/lib/editor-backend/context";
import { createMentionSuggestion } from "@/components/editor/extensions/mention-suggestion";
import { useMentionHover } from "@/components/editor/extensions/mention-hover";
import { Figure } from "@/components/editor/extensions/figure";
import { createTiptapDictationTarget } from "@/lib/dictation/insertion";
import { activateDictationTarget, registerDictationTarget } from "@/lib/dictation/target-registry";
export type ChapterEditorWorkspaceState = {
  isDirty: boolean;
  isSaving: boolean;
  wordCount: number;
  saveError: string | null;
};
export interface ChapterEditorProps {
  chapterId: string;
  storyId: string;
  chapterTitle: string;
  /** Current end-of-chapter author's note (TipTap JSON). Optional so the
   *  `/try` demo can omit it. */
  authorNote?: Record<string, unknown> | null;
  initialContent: Record<string, unknown> | null;
  initialWordCount: number;
  initialStatus: "draft" | "published";
  scheduledReleaseAt?: string | null;
  /**
   * Story-level publish state, forwarded to the toolbar so it can warn the
   * author when a freshly-published chapter won't reach readers yet. Optional
   * so the `/try` demo can omit them.
   */
  storyPublished?: boolean;
  storyVisibility?: "public" | "private";
  workspace?: {
    versionId: string;
    kind: ChapterDocumentKind;
    publicationStatus: "draft" | "published";
    publicationControls?: ReactNode;
    documentControls?: ReactNode;
    placeholder?: string;
    active: boolean;
    onFocus: () => void;
    onStateChange: (state: ChapterEditorWorkspaceState) => void;
  };
  saveRef?: Ref<ChapterSaveController>;
  readOnly?: boolean;
}
export function ChapterEditor(t0) {
  const $ = _c(116);
  const {
    chapterId,
    storyId,
    chapterTitle,
    authorNote,
    initialContent,
    initialStatus,
    scheduledReleaseAt,
    storyPublished,
    storyVisibility,
    workspace,
    saveRef,
    readOnly: t1
  } = t0;
  const readOnly = t1 === undefined ? false : t1;
  const backend = useEditorBackend();
  let t2;
  if ($[0] !== backend.searchWikiEntries || $[1] !== storyId) {
    t2 = createMentionSuggestion(storyId, backend.searchWikiEntries);
    $[0] = backend.searchWikiEntries;
    $[1] = storyId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const mentionSuggestion = t2;
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = StarterKit.configure({
      heading: {
        levels: [1, 2, 3]
      }
    });
    t4 = TextAlign.configure({
      types: ["heading", "paragraph"],
      alignments: ["left", "center", "right"]
    });
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== mentionSuggestion) {
    t5 = Mention.configure({
      HTMLAttributes: {
        class: "lore-mention"
      },
      suggestion: mentionSuggestion,
      renderText(t6) {
        const {
          node
        } = t6;
        return `@${node.attrs.label ?? node.attrs.id ?? "unknown"}`;
      }
    });
    $[5] = mentionSuggestion;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== workspace?.placeholder) {
    t6 = Placeholder.configure({
      placeholder: workspace?.placeholder ?? "Start writing your chapter\u2026"
    });
    $[7] = workspace?.placeholder;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = [t3, t4, t5, t6, Figure];
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = initialContent ?? undefined;
  let t9;
  if ($[12] !== t7 || $[13] !== t8) {
    t9 = {
      immediatelyRender: false,
      extensions: t7,
      content: t8
    };
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const editor = useEditor(t9);
  const t10 = workspace ? `${chapterId}:${workspace.versionId}` : chapterId;
  const t11 = !workspace;
  const t12 = !workspace;
  let t13;
  if ($[15] !== chapterId || $[16] !== editor || $[17] !== initialContent || $[18] !== initialStatus || $[19] !== t10 || $[20] !== t11 || $[21] !== t12) {
    t13 = {
      editor,
      chapterId,
      initialStatus,
      initialContent,
      recoveryKey: t10,
      manageStore: t11,
      syncMentions: t12
    };
    $[15] = chapterId;
    $[16] = editor;
    $[17] = initialContent;
    $[18] = initialStatus;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const save = useChapterSave(t13);
  let t14;
  if ($[23] !== save) {
    t14 = () => save;
    $[23] = save;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  useImperativeHandle(saveRef, t14);
  useEditorNavigation(save, !workspace);
  const {
    handleSave
  } = save;
  const onStateChange = workspace?.onStateChange;
  let t15;
  let t16;
  if ($[25] !== onStateChange || $[26] !== save.error || $[27] !== save.isDirty || $[28] !== save.isSaving || $[29] !== save.wordCount) {
    t15 = () => {
      onStateChange?.({
        isDirty: save.isDirty,
        isSaving: save.isSaving,
        wordCount: save.wordCount,
        saveError: save.error
      });
    };
    t16 = [onStateChange, save.isDirty, save.isSaving, save.wordCount, save.error];
    $[25] = onStateChange;
    $[26] = save.error;
    $[27] = save.isDirty;
    $[28] = save.isSaving;
    $[29] = save.wordCount;
    $[30] = t15;
    $[31] = t16;
  } else {
    t15 = $[30];
    t16 = $[31];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[32] !== editor || $[33] !== readOnly || $[34] !== save.isChangingStatus) {
    t17 = () => {
      if (editor && !editor.isDestroyed) {
        editor.setEditable(!readOnly && !save.isChangingStatus, false);
      }
    };
    t18 = [editor, readOnly, save.isChangingStatus];
    $[32] = editor;
    $[33] = readOnly;
    $[34] = save.isChangingStatus;
    $[35] = t17;
    $[36] = t18;
  } else {
    t17 = $[35];
    t18 = $[36];
  }
  useEffect(t17, t18);
  const onFocus = workspace?.onFocus;
  let t19;
  let t20;
  if ($[37] !== editor || $[38] !== onFocus) {
    t19 = () => {
      if (!editor || !onFocus) {
        return;
      }
      editor.on("focus", onFocus);
      return () => {
        editor.off("focus", onFocus);
      };
    };
    t20 = [editor, onFocus];
    $[37] = editor;
    $[38] = onFocus;
    $[39] = t19;
    $[40] = t20;
  } else {
    t19 = $[39];
    t20 = $[40];
  }
  useEffect(t19, t20);
  useMentionHover(editor, !backend.isDemo);
  let t21;
  let t22;
  if ($[41] !== handleSave || $[42] !== readOnly || $[43] !== workspace) {
    t21 = () => {
      const handleKeyDown = function handleKeyDown(e) {
        if (workspace && !workspace.active || readOnly) {
          return;
        }
        if ((e.metaKey || e.ctrlKey) && e.key === "s") {
          e.preventDefault();
          handleSave();
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    t22 = [handleSave, workspace, readOnly];
    $[41] = handleSave;
    $[42] = readOnly;
    $[43] = workspace;
    $[44] = t21;
    $[45] = t22;
  } else {
    t21 = $[44];
    t22 = $[45];
  }
  useEffect(t21, t22);
  const isMobile = useIsMobile();
  const autoHideToolbar = isMobile && !backend.isDemo;
  const scrollRef = useRef(null);
  const toolbarInnerRef = useRef(null);
  const lastScrollTop = useRef(0);
  const [toolbarHidden, setToolbarHidden] = useState(false);
  const [toolbarHeight, setToolbarHeight] = useState(undefined);
  let t23;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = () => {
      const el = toolbarInnerRef.current;
      if (!el) {
        return;
      }
      const measure = () => setToolbarHeight(el.offsetHeight);
      measure();
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    };
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== editor) {
    t24 = [editor];
    $[47] = editor;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  useEffect(t23, t24);
  let t25;
  if ($[49] !== autoHideToolbar) {
    t25 = () => {
      const el_0 = scrollRef.current;
      if (!el_0) {
        return;
      }
      const onScroll = () => {
        if (!autoHideToolbar) {
          setToolbarHidden(false);
          return;
        }
        const y = el_0.scrollTop;
        const last = lastScrollTop.current;
        if (y < 64) {
          setToolbarHidden(false);
        } else {
          if (y > last + 8) {
            setToolbarHidden(true);
          } else {
            if (y < last - 8) {
              setToolbarHidden(false);
            }
          }
        }
        lastScrollTop.current = y;
      };
      el_0.addEventListener("scroll", onScroll, {
        passive: true
      });
      return () => el_0.removeEventListener("scroll", onScroll);
    };
    $[49] = autoHideToolbar;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== autoHideToolbar || $[52] !== editor) {
    t26 = [editor, autoHideToolbar];
    $[51] = autoHideToolbar;
    $[52] = editor;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  useEffect(t25, t26);
  let t27;
  if ($[54] !== editor) {
    t27 = () => editor ? createTiptapDictationTarget(editor) : null;
    $[54] = editor;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  const lockChapterDictationTarget = t27;
  const versionId = workspace?.versionId;
  const isActiveVersion = workspace?.active;
  let t28;
  let t29;
  if ($[56] !== backend.isDemo || $[57] !== chapterId || $[58] !== editor || $[59] !== lockChapterDictationTarget || $[60] !== versionId) {
    t28 = () => {
      if (!editor || backend.isDemo) {
        return;
      }
      const targetId = `chapter:${chapterId}${versionId ? `:${versionId}` : ""}`;
      const activate = () => activateDictationTarget(targetId);
      const unregister = registerDictationTarget({
        id: targetId,
        label: "chapter",
        isFocused: () => editor.isFocused,
        lockTarget: lockChapterDictationTarget
      });
      editor.on("focus", activate);
      if (editor.isFocused) {
        activate();
      }
      return () => {
        editor.off("focus", activate);
        unregister();
      };
    };
    t29 = [backend.isDemo, chapterId, editor, lockChapterDictationTarget, versionId];
    $[56] = backend.isDemo;
    $[57] = chapterId;
    $[58] = editor;
    $[59] = lockChapterDictationTarget;
    $[60] = versionId;
    $[61] = t28;
    $[62] = t29;
  } else {
    t28 = $[61];
    t29 = $[62];
  }
  useEffect(t28, t29);
  let t30;
  let t31;
  if ($[63] !== backend.isDemo || $[64] !== chapterId || $[65] !== editor || $[66] !== isActiveVersion || $[67] !== versionId) {
    t30 = () => {
      if (editor && !backend.isDemo && versionId && isActiveVersion) {
        activateDictationTarget(`chapter:${chapterId}:${versionId}`);
      }
    };
    t31 = [backend.isDemo, chapterId, editor, versionId, isActiveVersion];
    $[63] = backend.isDemo;
    $[64] = chapterId;
    $[65] = editor;
    $[66] = isActiveVersion;
    $[67] = versionId;
    $[68] = t30;
    $[69] = t31;
  } else {
    t30 = $[68];
    t31 = $[69];
  }
  useEffect(t30, t31);
  if (!editor) {
    let t32;
    if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = <div className="flex flex-col h-full" />;
      $[70] = t32;
    } else {
      t32 = $[70];
    }
    return t32;
  }
  let t32;
  if ($[71] !== autoHideToolbar || $[72] !== toolbarHeight || $[73] !== toolbarHidden) {
    t32 = autoHideToolbar ? {
      maxHeight: toolbarHidden ? 0 : toolbarHeight,
      opacity: toolbarHidden ? 0 : 1
    } : undefined;
    $[71] = autoHideToolbar;
    $[72] = toolbarHeight;
    $[73] = toolbarHidden;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  const t33 = autoHideToolbar && toolbarHidden;
  const t34 = save.isChangingStatus || readOnly;
  const t35 = workspace?.publicationStatus ?? save.status;
  const t36 = save.changeStatus;
  const t37 = save.isChangingStatus;
  const t38 = save.error ?? save.statusError;
  const t39 = Boolean(workspace);
  const t40 = workspace?.kind === "notes";
  const t41 = workspace?.publicationControls;
  const t42 = workspace?.documentControls;
  const t43 = workspace ? save : undefined;
  let t44;
  if ($[75] !== authorNote || $[76] !== chapterId || $[77] !== chapterTitle || $[78] !== editor || $[79] !== handleSave || $[80] !== save.changeStatus || $[81] !== save.isChangingStatus || $[82] !== scheduledReleaseAt || $[83] !== storyId || $[84] !== storyPublished || $[85] !== storyVisibility || $[86] !== t35 || $[87] !== t38 || $[88] !== t39 || $[89] !== t40 || $[90] !== t41 || $[91] !== t42 || $[92] !== t43) {
    t44 = <EditorToolbar editor={editor} chapterId={chapterId} storyId={storyId} chapterTitle={chapterTitle} authorNote={authorNote} status={t35} scheduledReleaseAt={scheduledReleaseAt} onChangeStatus={t36} isTogglingStatus={t37} saveError={t38} onSave={handleSave} storyPublished={storyPublished} storyVisibility={storyVisibility} hidePublication={t39} isNotes={t40} publicationControls={t41} documentControls={t42} saveState={t43} />;
    $[75] = authorNote;
    $[76] = chapterId;
    $[77] = chapterTitle;
    $[78] = editor;
    $[79] = handleSave;
    $[80] = save.changeStatus;
    $[81] = save.isChangingStatus;
    $[82] = scheduledReleaseAt;
    $[83] = storyId;
    $[84] = storyPublished;
    $[85] = storyVisibility;
    $[86] = t35;
    $[87] = t38;
    $[88] = t39;
    $[89] = t40;
    $[90] = t41;
    $[91] = t42;
    $[92] = t43;
    $[93] = t44;
  } else {
    t44 = $[93];
  }
  let t45;
  if ($[94] !== t34 || $[95] !== t44) {
    t45 = <div ref={toolbarInnerRef} inert={t34}>{t44}</div>;
    $[94] = t34;
    $[95] = t44;
    $[96] = t45;
  } else {
    t45 = $[96];
  }
  let t46;
  if ($[97] !== t32 || $[98] !== t33 || $[99] !== t45) {
    t46 = <div className="shrink-0 overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out" style={t32} aria-hidden={t33}>{t45}</div>;
    $[97] = t32;
    $[98] = t33;
    $[99] = t45;
    $[100] = t46;
  } else {
    t46 = $[100];
  }
  let t47;
  if ($[101] !== readOnly || $[102] !== save.discardRecovery || $[103] !== save.isChangingStatus || $[104] !== save.recovery || $[105] !== save.restoreRecovery) {
    t47 = save.recovery && <div role="status" className="flex flex-wrap items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100"><span className="flex-1">Unsaved writing from this tab is available.</span><button type="button" onClick={save.restoreRecovery} disabled={save.isChangingStatus || readOnly} className="font-medium underline underline-offset-2">Restore writing</button><button type="button" onClick={save.discardRecovery} disabled={save.isChangingStatus || readOnly} className="underline underline-offset-2">Discard recovery</button></div>;
    $[101] = readOnly;
    $[102] = save.discardRecovery;
    $[103] = save.isChangingStatus;
    $[104] = save.recovery;
    $[105] = save.restoreRecovery;
    $[106] = t47;
  } else {
    t47 = $[106];
  }
  let t48;
  if ($[107] !== save.recoveryUnavailable) {
    t48 = save.recoveryUnavailable && <p role="alert" className="px-4 py-2 text-sm text-destructive">Local recovery is unavailable. Keep this tab open until your changes are saved.</p>;
    $[107] = save.recoveryUnavailable;
    $[108] = t48;
  } else {
    t48 = $[108];
  }
  let t49;
  if ($[109] !== editor) {
    t49 = <div ref={scrollRef} className="flex flex-1 flex-col overflow-y-auto bg-zinc-100 pb-[calc(var(--editor-mobile-nav-height)+env(safe-area-inset-bottom))] dark:bg-zinc-950 md:pb-0"><div className="chapter-editor-content rich-content mx-auto my-4 min-h-[calc(100%-2rem)] w-full max-w-[816px] flex-none rounded bg-white shadow-[0_1px_3px_rgba(0,0,0,0.12),0_1px_2px_rgba(0,0,0,0.08)] ring-1 ring-zinc-200/60 dark:bg-zinc-900 dark:ring-zinc-800 sm:my-8 sm:min-h-[1056px]"><EditorContent editor={editor} /></div></div>;
    $[109] = editor;
    $[110] = t49;
  } else {
    t49 = $[110];
  }
  let t50;
  if ($[111] !== t46 || $[112] !== t47 || $[113] !== t48 || $[114] !== t49) {
    t50 = <div className="flex flex-col h-full">{t46}{t47}{t48}{t49}</div>;
    $[111] = t46;
    $[112] = t47;
    $[113] = t48;
    $[114] = t49;
    $[115] = t50;
  } else {
    t50 = $[115];
  }
  return t50;
}